import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchCriterion, updateCriterion, createCriterion, deleteCriterion } from "Redux/Admin/DecisionManager/Criteria/action";

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";

import type { ICriterion } from 'Redux/Admin/DecisionManager/Criteria/type';

const Criterion = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchCriteria);

  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchCriterion(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createCriterion(values, handleCreate));
    }
    else if (id) {
      dispatch(updateCriterion(values, id));
    }
  };

  const handleCreate = (project: ICriterion) => {
    if (project && project.id) {
      navigate(`${ADMIN_PATH}/decision-manager/criteria/${project.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/decision-manager/criteria`, { replace: true });
    return null;
  }

  const criterion = isNew ? initialValues : entities[id];

  if (!criterion) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: 'Delete Criterion',
      key: '2',
      danger: true,
    }
  ];

  const modalConfig = {
    title: 'Delete the criterion?',
    content: <span>Are you sure you want to delete the criterion <b>{criterion.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '2') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteCriterion(id, () => {
          navigate(`${ADMIN_PATH}/decision-manager/criteria`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/decision-manager/criteria`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } Criterion`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage Criterion
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={criterion}
                errorData={error}
                isNew={isNew}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default Criterion;
